import produce from 'immer'
import { constants } from './actions'
import { commonConstants } from '../common/actions'

const { REQUEST, SUCCESS, FAILURE } = commonConstants
const { SUBMIT_LESSON_FORM, FETCH_LESSON_LIST, DELETE_LESSON, UPDATE_LESSON,
  RESET_LESSON_FORM_FIELDS, FETCH_LESSON, UPDATE_LESSON_FORM_FIELD,
  ADD_QUESTION, REMOVE_QUESTION, UPDATE_QUESTION,
  ADD_ANSWER, REMOVE_ANSWER, UPDATE_ANSWER, UPDATE_LESSON_RESPONSE,
  FETCH_LESSON_RESPONSE, UPDATE_QUESTION_DESCRIPTION, UPDATE_ANSWER_DESCRIPTION,
  UPDATE_ANSWER_CORRECT, UPDATE_QUESTION_TYPE
} = constants

const initState = {
  id: null,
  title: '',
  description: '',
  videoUrl: null,
  documentUrl: null,
  questions: [],
  lessonResponse: { responses: [] },
  // True only once the saved answers for lessonResponse.lesson have loaded.
  // Answers can't be changed or saved before that.
  lessonResponseLoaded: false,
  // async
  list: [],
  loading: {},
  error: {},
  success: {},
}

const emptyLessonResponse = (lesson) => ({ lesson, responses: [] })

// Lesson ids arrive as numbers from the API and as strings from the URL.
export const isSameLesson = (a, b) => a != null && b != null && String(a) === String(b)

// The in-memory answers belong to `lesson` and have finished loading.
export const canAnswerLesson = (lessonsState, lesson) =>
  lessonsState.lessonResponseLoaded && isSameLesson(lessonsState.lessonResponse.lesson, lesson)

export default function reducer (state = initState, action) {
  switch (action.type) {
    case UPDATE_LESSON_FORM_FIELD:
      return produce(state, draft => {
        draft[action.field] = action.value
        draft.loading = {}
        draft.error = {}
        draft.success = {}
      })
    case RESET_LESSON_FORM_FIELDS:
      const success = { source: RESET_LESSON_FORM_FIELDS, message: 'All form fields were successfully reset.' }
      if (!action.payload.showMessage) { delete success.message }
      state = {
        ...initState,
        success
      }
      return state
    case SUBMIT_LESSON_FORM + REQUEST:
      return produce(state, draft => {
        draft.loading = { source: action.payload.source }
      })
    case SUBMIT_LESSON_FORM + SUCCESS:
      console.log("SUCCESS")
      return produce(state, draft => {
        draft.success = { source: action.payload.source, message: action.payload.message }
      })
    case SUBMIT_LESSON_FORM + FAILURE:
      console.log("FAILURE")
      return produce(state, draft => {
        draft.error = { source: action.payload.source, message: action.payload.message }
      })
    case FETCH_LESSON_LIST + REQUEST:
      return produce(state, draft => {
        draft.loading = { source: action.source }
      })
    case FETCH_LESSON_LIST + SUCCESS:
      return produce(state, draft => {
        draft.success = action.payload
        draft.loading = {}
        draft.list = action.payload.list.sort((a, b) => {
          return b.id - a.id
        })
      })
    case FETCH_LESSON_LIST + FAILURE:
      return produce(state, draft => {
        draft.error = { source: action.payload.source, message: action.payload.message }
        draft.loading = {}
      })
    case DELETE_LESSON + REQUEST:
      return produce(state, draft => {
        draft.loading = { source: action.source }
      })
    case DELETE_LESSON + SUCCESS:
      return produce(state, draft => {
        draft.success = { source: action.payload.source, message: action.payload.message }
        draft.list = action.payload.list.sort((a, b) => {
          return b.id - a.id
        })
        draft.loading = {}
      })
    case DELETE_LESSON + FAILURE:
      return produce(state, draft => {
        draft.error = { source: action.payload.source, message: action.payload.message }
        draft.loading = {}
      })
    case UPDATE_LESSON + REQUEST:
      return produce(state, draft => {
        draft.loading = { source: UPDATE_LESSON }
      })
    case UPDATE_LESSON + SUCCESS:
      return produce(state, draft => {
        draft.loading = {}
        draft.success = action.payload
      })
    case UPDATE_LESSON + FAILURE:
      return produce(state, draft => {
        draft.loading = {}
        draft.error = action.payload
      })
    case FETCH_LESSON + REQUEST:
      return produce(state, draft => {
        draft.loading = { source: FETCH_LESSON }
      })
    case FETCH_LESSON + SUCCESS:
      const { id, title, videoUrl, documentUrl, description,
        lesson_type, questions, completed } = action.payload.lesson
      return produce(state, draft => {
        draft.loading = {}
        draft.success = action.payload
        draft.id = id
        draft.title = title
        draft.description = description
        draft.lesson_type = lesson_type
        draft.videoUrl = videoUrl
        draft.video_url = videoUrl
        draft.documentUrl = documentUrl
        draft.document_url = documentUrl
        draft.questions = questions
        draft.completed = completed
      })
    case FETCH_LESSON + FAILURE:
      return produce(state, draft => {
        draft.loading = {}
        draft.error = action.payload
      })
    case ADD_QUESTION + REQUEST:
      return produce(state, draft => {
        draft.questions.push({ question: '', description: '', question_type: 'single', answers: [] })
      })
    case REMOVE_QUESTION + REQUEST:
      return produce(state, draft => {
        draft.questions.splice(action.payload.index, 1);
      })
    case UPDATE_QUESTION + REQUEST:
      return produce(state, draft => {
        draft.questions[action.payload.index].question = action.payload.text
      })
    case UPDATE_QUESTION_DESCRIPTION + REQUEST:
      return produce(state, draft => {
        draft.questions[action.payload.index].description = action.payload.text
      })
    case UPDATE_QUESTION_TYPE + REQUEST:
      return produce(state, draft => {
        draft.questions[action.payload.index].question_type = action.payload.value
      })
    case ADD_ANSWER + REQUEST:
      return produce(state, draft => {
        draft.questions[action.payload.question_index].answers.push({ answer: '', description: '', correct: false })
      })
    case REMOVE_ANSWER + REQUEST:
      return produce(state, draft => {
        draft.questions[action.payload.question_index].answers.splice(action.payload.index, 1);
      })
    case UPDATE_ANSWER + REQUEST:
      return produce(state, draft => {
        draft.questions[action.payload.question_index].answers[action.payload.index].answer = action.payload.text
      })
    case UPDATE_ANSWER_DESCRIPTION + REQUEST:
      return produce(state, draft => {
        draft.questions[action.payload.question_index].answers[action.payload.index].description = action.payload.text
      })
    case UPDATE_ANSWER_CORRECT + REQUEST:
      return produce(state, draft => {
        draft.questions[action.payload.question_index].answers[action.payload.index].correct = action.payload.value
      })
    case FETCH_LESSON_RESPONSE + REQUEST:
      return produce(state, draft => {
        draft.loading = { source: action.payload.source }
        // Forget the previous module's answers (and row id) straight away, so
        // nothing can be answered or saved against them while this one loads.
        draft.lessonResponse = emptyLessonResponse(action.payload.lesson)
        draft.lessonResponseLoaded = false
      })
    case FETCH_LESSON_RESPONSE + SUCCESS:
      return produce(state, draft => {
        if (!isSameLesson(draft.lessonResponse.lesson, action.payload.lesson)) return
        draft.lessonResponse = {
          ...(action.payload.lessonResponse ?? emptyLessonResponse(action.payload.lesson)),
          lesson: action.payload.lesson,
        }
        draft.lessonResponseLoaded = true
      })
    case FETCH_LESSON_RESPONSE + FAILURE:
      return produce(state, draft => {
        draft.error = { source: action.payload.source, message: action.payload.message }
        if (!isSameLesson(draft.lessonResponse.lesson, action.payload.lesson)) return
        // We don't know what this volunteer saved, so keep answering disabled
        // rather than risk overwriting (or duplicating) their record.
        draft.lessonResponse = emptyLessonResponse(action.payload.lesson)
        draft.lessonResponseLoaded = false
      })
    case UPDATE_LESSON_RESPONSE + REQUEST:
      return produce(state, draft => {
        if (!canAnswerLesson(draft, action.payload.id)) return
        draft.lessonResponse.responses[action.payload.question_index] = action.payload.index
      })
    case UPDATE_LESSON_RESPONSE + SUCCESS:
      return produce(state, draft => {
        // A save can finish after the volunteer has moved to another module;
        // don't let it replace that module's answers.
        if (!isSameLesson(draft.lessonResponse.lesson, action.payload.lesson)) return
        draft.lessonResponse = { ...action.payload.lessonResponse, lesson: action.payload.lesson }
      })
    default:
      return state
  }
}
